/**
 * Dell Switch Database
 *
 * Dell PowerSwitch switches for AI/ML GPU clusters.
 *
 * Data sources: research/switch-specs.md
 */

import type { SwitchModel } from '../../types/switches';

/**
 * Dell PowerSwitch Z9664F-ON (400GbE, RoCEv2)
 */
export const DELL_Z9664F_ON: SwitchModel = {
  id: 'dell-z9664f-on',
  name: 'Z9664F-ON',
  vendor: 'Dell',
  productName: 'Dell PowerSwitch Z9664F-ON',
  fabricType: 'RoCE',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 400,
      unit: 'Gbps',
      displayName: '400GbE',
    },
    portType: 'QSFP56-DD',
    supportsBreakout: true,
    breakoutOptions: ['64x400G', '128x200G', '256x100G'],
  },
  power: {
    typicalWatts: 500,
    maxWatts: 2125,
    idleWatts: 280,
    perPortWatts: 18,
    psuConfig: {
      count: 2,
      capacity: 2500,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 438,
    depthMm: 620,
    weightKg: 18,
    airflow: 'front-to-back',
    maxBtuPerHour: 7250,
  },
  performance: {
    switchingCapacityTbps: 51.2,
    forwardingRateMpps: 38400,
    latencyNs: 350,
    bufferSizeMB: 96,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true, // RoCEv2 optimized
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: [
      'RoCEv2-based AI Fabric',
      'Supports 128 GPU scale with single switch',
      '200G rollout capability',
      'Priority-based Flow Control (PFC)',
      'Data Center Bridge Exchange (DCBX)',
      'Enhanced Transmission Selection (ETS)',
      'Dell OS10',
    ],
  },
  metadata: {
    productUrl: 'https://www.delltechnologies.com/asset/en-us/products/networking/technical-support/dell-powerswitch-z9664f-on-spec-sheet.pdf',
    releaseYear: 2022,
    endOfLife: false,
    notes: 'RoCEv2-optimized for AI/ML distributed training and fine-tuning. Full line-rate performance.',
  },
};

/**
 * All Dell switches
 */
export const DELL_SWITCHES: readonly SwitchModel[] = [
  DELL_Z9664F_ON,
] as const;

/**
 * Get Dell switch by ID
 */
export function getDellSwitch(id: string): SwitchModel | undefined {
  return DELL_SWITCHES.find((sw) => sw.id === id);
}
